import { ViewportScroller } from '@angular/common';
import { Component, DOCUMENT, HostListener, inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Params, Router, Scroll } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { MatomoTracker } from 'ngx-matomo-client';
import { of } from 'rxjs';
import { catchError, filter, switchMap, tap, timeout } from 'rxjs/operators';

import { ACCESS_ROUTE } from 'app/access/access-routing.constant';
import { faIcons } from 'app/core/app-constants/used-fa-icons';
import { AuthenticationService } from 'app/core/authentication';
import { TOKEN_STORAGE_KEYS } from 'app/core/authentication/constants/token-storage-keys';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { TranslationLoaderHelper } from 'app/core/internationalization/translation-loader-helper';
import { RouterService } from 'app/core/navigation';
import { SessionRefreshCoordinatorService } from 'app/core/session-refresh/services/session-refresh-coordinator.service';
import { ThemesService } from 'app/core/themes/themes.service';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { SingleSignOnService } from 'app/shared/services/single-sign-on.service';

import { environment } from 'env/environment';

interface PostLogoutRedirect {
  params: Params;
  reload?: boolean;
  url: string[];
}

// A stalled SSO client must never strand the user on a page they just logged out of.
const SSO_LOGOUT_TIMEOUT_MS = 3000;

@UntilDestroy()
@Component({
  selector: 'kh-app-root',
  styleUrls: ['./app.component.less'],
  templateUrl: './app.component.html',
  standalone: false
})

export class AppComponent implements OnInit {
  public title = 'Web App';
  public themeUrl?: SafeResourceUrl;

  private readonly tracker = inject(MatomoTracker);
  private readonly translationService = inject(TranslationService);
  private readonly translationLoader = inject(TranslationLoaderHelper);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly themesService = inject(ThemesService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly singleSign = inject(SingleSignOnService);
  private readonly routerService = inject(RouterService);
  private readonly sessionRefreshCoordinator = inject(SessionRefreshCoordinatorService);
  private readonly document = inject(DOCUMENT);
  private readonly library = inject(FaIconLibrary);
  private readonly router = inject(Router);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  public readonly khAnalyticsService = inject(KhAnalyticsService);

  public translationsLoading$ = this.translationLoader.loading$;

  public constructor() {
    this.authenticationService.isAuthorized()
      .pipe(tap((value) => {
        let locale;
        let isBetaLocale = false;
        if (value) {
          const betaItem = sessionStorage.getItem(TranslationService.BETA_SESSION_LANGUAGE_KEY);
          const betaLocale = betaItem ? this.getStoredLocaleId(JSON.parse(betaItem)) : undefined;
          locale = betaLocale ?? this.authenticationService.getUserLocale();
          isBetaLocale = !!betaLocale;
        }
        else {
          const item = sessionStorage.getItem(TranslationService.SESSION_LANGUAGE_KEY);
          if (item) {
            const parsedLanguage: unknown = JSON.parse(item);
            locale = this.getStoredLocaleId(parsedLanguage) ?? TranslationService.DEFAULT_LANGUAGE;
          }
          else {
            locale = TranslationService.DEFAULT_LANGUAGE;
          }
        }
        this.translationService.loadLanguage(locale, isBetaLocale);
      }))
      .subscribe();

    this.library.addIcons(...(faIcons));

    this.router.events
      .pipe(
        filter((event): event is Scroll => event instanceof Scroll),
        untilDestroyed(this))
      .subscribe((e) => {
        if (!this.router.currentNavigation()?.extras?.state?.suppressScroll) {
          this.viewportScroller.scrollToPosition(e.position ?? [0, 0]);
        }
      });
  }

  @HostListener('window:unload', ['$event']) protected handleUnload() {
    this.khAnalyticsService.flushEvents();
  }

  public ngOnInit(): void {
    if (!this.sessionRefreshCoordinator.handlePendingSessionExpiry()) {
      this.sessionRefreshCoordinator.start();
    }

    this.authenticationService.isAuthorized()
      .pipe(
        tap(() => { this.setupMatomoUser(); }),
        untilDestroyed(this)
      )
      .subscribe();

    this.document.documentElement.lang = this.translationService.getCurrentLanguage().split('-')[0];

    this.themesService.themeChanged()
      .pipe(
        tap((theme) => this.themeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(theme)),
        untilDestroyed(this)
      )
      .subscribe();

    this.authenticationService.userLoggedIn()
      .pipe(
        tap(() => { this.sessionRefreshCoordinator.start(); }),
        tap(() => { sessionStorage.removeItem('MilestonesModalShown'); }),
        tap(() => { this.setupMatomoUser(); }),
        untilDestroyed(this)
      )
      .subscribe();

    this.authenticationService.userLoggedOut()
      .pipe(
        tap(() => { this.sessionRefreshCoordinator.stop(); }),
        tap(() => { this.resetMatomoUser(); }),
        switchMap(() => this.singleSign.logoutAll()
          .pipe(
            timeout(SSO_LOGOUT_TIMEOUT_MS),
            catchError((error: unknown) => {
              console.error(error);

              return of(undefined);
            })
          )),
        tap(() => {
          const postLogoutRedirectString = localStorage.getItem(TOKEN_STORAGE_KEYS.POST_LOGOUT_REDIRECT);
          if (postLogoutRedirectString) {
            const postLogoutRedirect = JSON.parse(postLogoutRedirectString) as PostLogoutRedirect;
            localStorage.removeItem(TOKEN_STORAGE_KEYS.POST_LOGOUT_REDIRECT);
            this.navigateAfterLogout(postLogoutRedirect);
          }
          else {
            this.routerService.navigate(ACCESS_ROUTE.route()).catch(() => { console.error('Navigation error'); });
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.oidcSecurityService.checkAuth(environment.mceClientId).subscribe();
  }

  private navigateAfterLogout(postLogoutRedirect: PostLogoutRedirect): void {
    this.router.navigate(postLogoutRedirect.url, {
      queryParams: postLogoutRedirect.params
    })
      .then(
        () => {
          if (postLogoutRedirect.reload) {
            this.document.defaultView?.location.reload();
          }
        },
        () => {
          console.error('Navigation error');
          if (postLogoutRedirect.reload) {
            this.document.defaultView?.location.reload();
          }
        }
      );
  }

  private setupMatomoUser() {
    const claims = this.authenticationService.getUserClaims();
    if (!claims) { return; }

    this.tracker.setUserId(claims.userId);
    const roles = this.authenticationService.roles().join(',');
    const roleDimesions = ['Student', 'Parent', 'Teacher'];
    for (let i = 0; i < roleDimesions.length; i++) {
      if (roles.includes(roleDimesions[i])) {
        this.tracker.setCustomDimension((i + 1), roleDimesions[i]);
      }
    }
  }

  private resetMatomoUser() {
    this.tracker.resetUserId();
    this.tracker.deleteCustomDimension(1);
    this.tracker.deleteCustomDimension(2);
    this.tracker.deleteCustomDimension(3);
  }

  private getStoredLocaleId(value: unknown): string | undefined {
    if (!value || typeof value !== 'object' || !('LocaleId' in value)) {
      return undefined;
    }

    const localeId = (value as { LocaleId?: unknown }).LocaleId;
    return typeof localeId === 'string' ? localeId : undefined;
  }
}
